import {
  createWorkplaceColorSpectrumColorEntries,
  getBlockDefaultInteractionMessage,
  getBlockDefaultRequireInteraction,
  type AuthoringBlockType,
  type CourseBlock,
} from '@course-authoring/shared-types';

export type BlockTypeOption = AuthoringBlockType;

export const generateTempBlockId = () =>
  `tmp_${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const generateSubItemId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sub_${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
};

function getInteractionDefaults(
  blockType: CourseBlock['type']
): Pick<CourseBlock, 'requireInteraction' | 'interactionMessage'> {
  const defaults: Pick<CourseBlock, 'requireInteraction' | 'interactionMessage'> = {};
  const defaultRequireInteraction = getBlockDefaultRequireInteraction(blockType);
  const defaultInteractionMessage = getBlockDefaultInteractionMessage(blockType);

  if (defaultRequireInteraction !== undefined) {
    defaults.requireInteraction = defaultRequireInteraction;
  }

  if (blockType !== 'map' && defaultInteractionMessage) {
    defaults.interactionMessage = defaultInteractionMessage;
  }

  return defaults;
}

export function createBlockFromType(type: BlockTypeOption): CourseBlock {
  const now = new Date().toISOString();
  const id = generateTempBlockId();

  switch (type) {
    case 'basic-content-a':
      return {
        id,
        type: 'basic-content-a',
        layout: 'image-top',
        imageSize: 'medium',
        imageUrl: undefined,
        imageAlt: '',
        titleLevel: 'h1',
        title: '',
        body: {
          type: 'doc',
          content: [{ type: 'paragraph' }],
        },
        aspectRatio: '2:1',
        createdAt: now,
        updatedAt: now,
      };

    case 'stylized-text-callout':
      return {
        id,
        type: 'stylized-text-callout',
        content: {
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  marks: [{ type: 'italic' }],
                  text: '',
                },
              ],
            },
          ],
        },
        textSize: 'standard',
        createdAt: now,
        updatedAt: now,
      };

    case 'stylized-numbered-list':
      return {
        id,
        type: 'stylized-numbered-list',
        title: '',
        titleLevel: 'h3',
        items: [
          {
            id: generateSubItemId(),
            content: { type: 'doc', content: [{ type: 'paragraph' }] },
          },
        ],
        listStyle: 'numbered',
        createdAt: now,
        updatedAt: now,
      };

    case 'stylized-sidebar-callout':
      return {
        id,
        type: 'stylized-sidebar-callout',
        title: { type: 'doc', content: [{ type: 'paragraph' }] },
        titleLevel: 'h2',
        body: { type: 'doc', content: [{ type: 'paragraph' }] },
        calloutTitle: { type: 'doc', content: [{ type: 'paragraph' }] },
        calloutBody: { type: 'doc', content: [{ type: 'paragraph' }] },
        createdAt: now,
        updatedAt: now,
      };

    case 'comparison-slider':
      return {
        id,
        type: 'comparison-slider',
        contentType: 'text-image',
        imageSize: 'small',
        titleLevel: 'h2',
        ...getInteractionDefaults('comparison-slider'),
        title: { type: 'doc', content: [{ type: 'paragraph' }] },
        subtitle: { type: 'doc', content: [{ type: 'paragraph' }] },
        option1: {
          collapsedLabel: 'Click the slider to see Option 2',
          title: { type: 'doc', content: [{ type: 'paragraph' }] },
          body: { type: 'doc', content: [{ type: 'paragraph' }] },
        },
        option2: {
          collapsedLabel: 'Click the slider to see Option 1',
          title: { type: 'doc', content: [{ type: 'paragraph' }] },
          body: { type: 'doc', content: [{ type: 'paragraph' }] },
        },
        createdAt: now,
        updatedAt: now,
      };

    case 'click-expand':
      return {
        id,
        type: 'click-expand',
        title: '',
        titleLevel: 'h2',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        expandType: 'accordion',
        imageSize: 'small',
        ...getInteractionDefaults('click-expand'),
        topics: [
          {
            id: generateSubItemId(),
            title: 'Topic 1',
            content: { type: 'doc', content: [{ type: 'paragraph' }] },
          },
          {
            id: generateSubItemId(),
            title: 'Topic 2',
            content: { type: 'doc', content: [{ type: 'paragraph' }] },
          },
        ],
        createdAt: now,
        updatedAt: now,
      };

    case 'workplace-color-spectrum':
      return {
        id,
        type: 'workplace-color-spectrum',
        ...getInteractionDefaults('workplace-color-spectrum'),
        title: 'The Workplace Color Spectrum\u00AE',
        titleLevel: 'h2',
        description: {
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [
                {
                  type: 'text',
                  text: "Rate the manager's initial reaction to the employee's report.",
                },
              ],
            },
          ],
        },
        colors: createWorkplaceColorSpectrumColorEntries(),
        correctAnswer: 'yellow',
        correctAnswerExplanation: { type: 'doc', content: [{ type: 'paragraph' }] },
        createdAt: now,
        updatedAt: now,
      };

    case 'policy-acknowledgement':
      return {
        id,
        type: 'policy-acknowledgement',
        title: '',
        titleLevel: 'h2',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        policyType: 'pdf',
        policyUrl: '',
        ...getInteractionDefaults('policy-acknowledgement'),
        createdAt: now,
        updatedAt: now,
      };

    case 'electronic-signature':
      return {
        id,
        type: 'electronic-signature',
        title: 'Electronic Signature and Acknowledgement',
        titleLevel: 'h1',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        checkboxLabel: 'I have completed this course.',
        ...getInteractionDefaults('electronic-signature'),
        createdAt: now,
        updatedAt: now,
      };

    case 'map':
      return {
        id,
        type: 'map',
        mapVersionId: '',
        title: '',
        titleLevel: 'h2',
        description: '',
        ...getInteractionDefaults('map'),
        createdAt: now,
        updatedAt: now,
      };

    case 'categorization':
      return {
        id,
        type: 'categorization',
        ...getInteractionDefaults('categorization'),
        title: '',
        titleLevel: 'h2',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        categories: [
          { id: generateSubItemId(), label: '' },
          { id: generateSubItemId(), label: '' },
        ],
        items: [
          { id: generateSubItemId(), label: '', correctCategoryId: '' },
          { id: generateSubItemId(), label: '', correctCategoryId: '' },
          { id: generateSubItemId(), label: '', correctCategoryId: '' },
          { id: generateSubItemId(), label: '', correctCategoryId: '' },
          { id: generateSubItemId(), label: '', correctCategoryId: '' },
        ],
        correctFeedbackHeading: 'Perfect!',
        correctFeedbackBody: {
          type: 'doc',
          content: [{ type: 'paragraph', content: [{ type: 'text', text: 'You correctly categorized all of the items.' }] }],
        },
        incorrectFeedbackHeading: 'Not quite right',
        incorrectFeedbackBody: {
          type: 'doc',
          content: [{ type: 'paragraph', content: [{ type: 'text', text: 'Some items are incorrectly placed. Review and adjust your answers.' }] }],
        },
        createdAt: now,
        updatedAt: now,
      };

    case 'single-choice-answer':
      return {
        id,
        type: 'single-choice-answer',
        ...getInteractionDefaults('single-choice-answer'),
        title: '',
        titleLevel: 'h2',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        options: [
          { id: generateSubItemId(), label: '' },
          { id: generateSubItemId(), label: '' },
        ],
        gated: true,
        correctOptionId: '',
        correctExplanation: { type: 'doc', content: [{ type: 'paragraph' }] },
        createdAt: now,
        updatedAt: now,
      };

    case 'lesson-complete':
      return {
        id,
        type: 'lesson-complete',
        title: 'Lesson Complete',
        titleLevel: 'h2',
        description: {
          type: 'doc',
          content: [
            {
              type: 'paragraph',
              content: [
                { type: 'text', text: "Congratulations! You've completed the lesson." },
              ],
            },
          ],
        },
        createdAt: now,
        updatedAt: now,
      };

    case 'likert-scale':
      return {
        id,
        type: 'likert-scale',
        ...getInteractionDefaults('likert-scale'),
        title: '',
        titleLevel: 'h2',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        scale: 'agreement',
        optionCount: 7,
        healthyResponse: 'none',
        createdAt: now,
        updatedAt: now,
      };

    case 'link':
      return {
        id,
        type: 'link',
        title: '',
        titleLevel: 'h2',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        linkText: '',
        linkUrl: '',
        ...getInteractionDefaults('link'),
        createdAt: now,
        updatedAt: now,
      };

    case 'freeform-text':
      return {
        id,
        type: 'freeform-text',
        ...getInteractionDefaults('freeform-text'),
        title: '',
        titleLevel: 'h2',
        description: { type: 'doc', content: [{ type: 'paragraph' }] },
        createdAt: now,
        updatedAt: now,
      };

    case 'video':
      return {
        id,
        type: 'video',
        url: '',
        title: '',
        titleLevel: 'h2',
        shortDescription: '',
        longDescription: '',
        tracks: [
          {
            kind: 'captions',
            src: '',
            label: 'English',
            language: 'en',
          },
        ],
        ...getInteractionDefaults('video'),
        createdAt: now,
        updatedAt: now,
      };

    default: {
      const exhaustive: never = type;
      throw new Error(`Unhandled block type: ${String(exhaustive)}`);
    }
  }
}
